import { Dimensions, Platform, PixelRatio, useWindowDimensions } from 'react-native';

const { width: SCREEN_WIDTH, height: SCREEN_HEIGHT } = Dimensions.get('window');

// Design base dimensions (iPhone 12 Pro)
const baseWidth = 390;
const baseHeight = 844;

function scaleFor(size: number, screenWidth: number): number {
  let newSize: number;
  if (Platform.OS === 'web') {
    // Web: More conservative scaling to prevent oversized elements
    const webScaleFactor = Math.min(screenWidth / baseWidth, 1.5); // Max 1.5x scale
    newSize = size * webScaleFactor;
  } else {
    // Mobile: Standard scaling
    newSize = (size * screenWidth) / baseWidth;
  }
  return Math.round(PixelRatio.roundToNearestPixel(newSize));
}

function verticalScaleFor(size: number, screenHeight: number): number {
  let newSize: number;
  if (Platform.OS === 'web') {
    const webScaleFactor = Math.min(screenHeight / baseHeight, 1.3); // Max 1.3x scale
    newSize = size * webScaleFactor;
  } else {
    newSize = (size * screenHeight) / baseHeight;
  }
  return Math.round(PixelRatio.roundToNearestPixel(newSize));
}

function buildResponsive(screenWidth: number, screenHeight: number) {
  const scale = (size: number) => scaleFor(size, screenWidth);
  const verticalScale = (size: number) => verticalScaleFor(size, screenHeight);
  const moderateScale = (size: number, factor = 0.5) => size + (scale(size) - size) * factor;
  const platformScale = (iosSize: number, androidSize: number, webSize?: number) => {
    if (Platform.OS === 'ios') return scale(iosSize);
    if (Platform.OS === 'android') return scale(androidSize);
    return webSize ? scale(webSize) : scale(iosSize * 0.8); // web, 20% smaller than mobile if no webSize given
  };

  const isSmallScreen = screenWidth < 375;
  const isMediumScreen = screenWidth >= 375 && screenWidth < 414;
  const isLargeScreen = screenWidth >= 414 && screenWidth < 768;
  const isTablet = screenWidth >= 768;
  const isWeb = Platform.OS === 'web';

  const responsiveSpacing = {
    xs: scale(4),
    sm: scale(8),
    md: scale(16),
    lg: scale(24),
    xl: scale(32),
    '2xl': scale(48),
    '3xl': scale(64),
    '4xl': scale(96),
    screenPadding: isTablet ? scale(32) : isWeb ? scale(16) : scale(20),
    cardPadding: isTablet ? scale(24) : scale(16),
    buttonPadding: isTablet ? scale(16) : scale(12),
    inputPadding: isTablet ? scale(20) : scale(16),
  };

  const responsiveFontSize = {
    xs: scale(12),
    sm: scale(14),
    base: scale(16),
    lg: scale(18),
    xl: scale(20),
    '2xl': scale(24),
    '3xl': scale(30),
    '4xl': scale(36),
    '5xl': scale(48),
    '6xl': scale(60),
  };

  const responsiveDimensions = {
    screenWidth,
    screenHeight,
    isLandscape: screenWidth > screenHeight,
    isPortrait: screenWidth < screenHeight,
    headerHeight: platformScale(60, 64, 70),
    tabBarHeight: platformScale(80, 84, 80),
    buttonHeight: platformScale(48, 52, isWeb ? 44 : 50),
    inputHeight: platformScale(48, 52, isWeb ? 44 : 50),
    cardMargin: isTablet ? scale(24) : isWeb ? scale(12) : scale(16),
    sectionMargin: isTablet ? scale(32) : isWeb ? scale(20) : scale(24),
  };

  const webResponsive = {
    maxWidth: isWeb ? Math.min(screenWidth, 1200) : screenWidth,
    containerPadding: isWeb ? scale(24) : scale(20),
    cardWidth: isWeb ? Math.min(screenWidth * 0.8, 600) : ('100%' as const),
    scaleFactor: isWeb ? Math.min(screenWidth / baseWidth, 1.2) : 1,
    inputScale: isWeb ? 0.9 : 1,
    buttonScale: isWeb ? 0.95 : 1,
  };

  return {
    scale,
    verticalScale,
    moderateScale,
    platformScale,
    isSmallScreen,
    isMediumScreen,
    isLargeScreen,
    isTablet,
    isWeb,
    responsiveSpacing,
    responsiveFontSize,
    responsiveDimensions,
    webResponsive,
  };
}

/** Live-reactive equivalent of every export below — recomputes on window resize/rotation via
 * useWindowDimensions (unlike the module-level constants below, which are a one-time snapshot
 * frozen at whichever size the app happened to be when this module first loaded, and never
 * update after that — the root cause of the app's non-responsive-to-resize layout bugs).
 * Compute styles INSIDE the component body with this (not in a module-level
 * StyleSheet.create(), which can never re-run) wherever a screen must adapt live to resize —
 * e.g. WelcomeScreen.tsx, DualPaneLayout.tsx. */
export function useResponsive() {
  const { width, height } = useWindowDimensions();
  return buildResponsive(width, height);
}

// ─────────────────────────────────────────────────────────────────────────────
// Static snapshot exports (legacy) — computed ONCE at module load from
// Dimensions.get('window') and never updated. Kept for existing call sites not yet migrated
// to useResponsive(); safe for values that don't need to react to live resize (most
// Platform.OS-driven branching), but will drift on browser resize/rotation for anything
// genuinely screen-size-dependent. Prefer useResponsive() in new code.
// ─────────────────────────────────────────────────────────────────────────────
const staticResponsive = buildResponsive(SCREEN_WIDTH, SCREEN_HEIGHT);

export const scale = (size: number) => scaleFor(size, SCREEN_WIDTH);
export const verticalScale = (size: number) => verticalScaleFor(size, SCREEN_HEIGHT);
export const moderateScale = staticResponsive.moderateScale;
export const platformScale = staticResponsive.platformScale;
export const isSmallScreen = (): boolean => SCREEN_WIDTH < 375;
export const isMediumScreen = (): boolean => SCREEN_WIDTH >= 375 && SCREEN_WIDTH < 414;
export const isLargeScreen = (): boolean => SCREEN_WIDTH >= 414 && SCREEN_WIDTH < 768;
export const isTablet = (): boolean => SCREEN_WIDTH >= 768;
export const isWeb = (): boolean => Platform.OS === 'web';
export const responsiveSpacing = staticResponsive.responsiveSpacing;
export const responsiveFontSize = staticResponsive.responsiveFontSize;
export const responsiveDimensions = staticResponsive.responsiveDimensions;
export const webResponsive = staticResponsive.webResponsive;
